/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Card,
  CardContent,
  Slider,
  Switch,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import NetworkVisualizationOptions from './visualizations/NetworkVisualizationOptions';
import AdvancedNetworkViz from './visualizations/AdvancedNetworkViz';

// eslint-disable-next-line max-lines-per-function
const NetworkView = ({ hideControls = false, externalVisualization = null, onVisualizationChange = null, relationships = [] }) => {
  const [selectedVisualization, setSelectedVisualization] = useState('circular');
  const [filterByTrust, setFilterByTrust] = useState([1, 10]);
  const [showOnlyStrongRelationships, setShowOnlyStrongRelationships] = useState(false);
  const [_selectedGroup, setSelectedGroup] = useState('all');
  const [selectedEvolution, setSelectedEvolution] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('trust');
  const [useAdvancedViz, setUseAdvancedViz] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  const visualizationOptions = [
    { value: 'circular', label: 'Circular Network', description: 'Nodes arranged in circular pattern with colored relationship lines' },
    { value: 'clusters', label: 'Team Clusters', description: 'Grouped by organizational teams with cluster backgrounds' },
    { value: 'matrix', label: 'Relationship Matrix', description: 'Heatmap showing trust levels between all individuals' },
    { value: 'hierarchical', label: 'Organizational Tree', description: 'Traditional org chart based on reporting relationships' },
    { value: 'stable-force', label: 'Interactive Force Graph', description: 'Draggable network with metric-based coloring and stable physics' },
    { value: 'disjoint-force', label: 'Team-Based Force Graph', description: 'Force simulation with team clustering' },
    { value: 'arc-diagram', label: 'Arc Diagram', description: 'Linear arrangement with curved relationship arcs' },
    { value: 'patent-suits', label: 'Strength Clustering', description: 'Groups by relationship strength' },
    { value: 'radial-cluster', label: 'Radial Hierarchy', description: 'Circular hierarchical layout' },
    { value: 'tree-of-life', label: 'Centrality Tree', description: 'Tree based on relationship centrality' }
  ];

  const metricOptions = [
    { value: 'trust', label: 'Trust', description: 'Measures confidence and reliability in relationships' },
    { value: 'communication', label: 'Communication', description: 'Quality and frequency of information exchange' },
    { value: 'collaboration', label: 'Collaboration', description: 'Effectiveness of working together on shared goals' },
    { value: 'alignment', label: 'Alignment', description: 'Shared vision and values alignment' },
    { value: 'psychological_safety', label: 'Psychological Safety', description: 'Comfort with vulnerability and taking risks' }
  ];

  const getRelationshipStats = () => {
    if (!relationships || relationships.length === 0) {
      return {
        total: 0,
        avgTrust: '0.0',
        avgCollaboration: '0.0',
        avgAlignment: '0.0',
        evolution: {}
      };
    }

    const totalRelationships = relationships.length;
    const avgTrust = relationships.reduce((sum, rel) => sum + (rel.trust || 0), 0) / totalRelationships;
    const avgCollaboration = relationships.reduce((sum, rel) => sum + (rel.collaboration || 0), 0) / totalRelationships;
    const avgAlignment = relationships.reduce((sum, rel) => sum + (rel.alignment || 0), 0) / totalRelationships;

    const evolutionCounts = relationships.reduce((acc, rel) => {
      const evolution = rel.evolution || 'unknown';
      acc[evolution] = (acc[evolution] || 0) + 1;
      return acc;
    }, {});

    return {
      total: totalRelationships,
      avgTrust: avgTrust.toFixed(1),
      avgCollaboration: avgCollaboration.toFixed(1),
      avgAlignment: avgAlignment.toFixed(1),
      evolution: evolutionCounts
    };
  };

  const getFilteredRelationships = () => {
    if (!relationships || relationships.length === 0) return [];

    return relationships.filter(rel => {
      const trust = rel.trust || 0;
      if (trust < filterByTrust[0] || trust > filterByTrust[1]) return false;
      if (showOnlyStrongRelationships && trust < 7) return false;
      if (selectedEvolution !== 'all' && rel.evolution !== selectedEvolution) return false;
      return true;
    });
  };

  const stats = getRelationshipStats();
  const filteredRelationships = getFilteredRelationships();

  const handleVisualizationChange = (event) => {
    const newValue = event.target.value;
    setSelectedVisualization(newValue);
    if (onVisualizationChange) {
      onVisualizationChange(newValue);
    }
  };

  // Use external visualization if provided
  const currentVisualization = externalVisualization || selectedVisualization;

  const handleTrustFilterChange = (event, newValue) => {
    setFilterByTrust(newValue);
  };

  const resetFilters = () => {
    setFilterByTrust([1, 10]);
    setShowOnlyStrongRelationships(false);
    setSelectedGroup('all');
    setSelectedEvolution('all');
    setSelectedMetric('trust');
    setUseAdvancedViz(false);
  };

  // Get detailed information about a node (person/entity)
  const getNodeDetails = (nodeName) => {
    const nodeRelationships = filteredRelationships.filter(rel =>
      rel.entity1 === nodeName || rel.entity2 === nodeName
    );

    const relationships = nodeRelationships.map(rel => ({
      ...rel,
      partner: rel.entity1 === nodeName ? rel.entity2 : rel.entity1
    }));

    const avgScores = {
      trust: relationships.reduce((sum, r) => sum + r.trust, 0) / relationships.length || 0,
      communication: relationships.reduce((sum, r) => sum + r.communication, 0) / relationships.length || 0,
      psychological_safety: relationships.reduce((sum, r) => sum + r.psychological_safety, 0) / relationships.length || 0,
      collaboration: relationships.reduce((sum, r) => sum + r.collaboration, 0) / relationships.length || 0,
      alignment: relationships.reduce((sum, r) => sum + r.alignment, 0) / relationships.length || 0,
    };

    const strongRelationships = relationships.filter(r => r.trust >= 7);
    const evolvingRelationships = relationships.filter(r => r.evolution === 'strengthening' || r.evolution === 'improving');
    const concerningRelationships = relationships.filter(r => r.evolution === 'deteriorated' || r.trust < 4);

    return {
      name: nodeName,
      totalRelationships: relationships.length,
      strongRelationships,
      evolvingRelationships,
      concerningRelationships,
      avgScores,
      relationships
    };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Network Analysis
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Relationship dynamics based on comprehensive document analysis
      </Typography>
      {(!relationships || relationships.length === 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No relationship data available. Please provide relationship data to visualize the network.
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Controls Panel */}
        {!hideControls && (
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Visualization Controls</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={useAdvancedViz}
                  onChange={(e) => setUseAdvancedViz(e.target.checked)}
                />
              }
              label="Advanced Visualizations"
              sx={{ mb: 2 }}
            />

            {!useAdvancedViz && !hideControls && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Visualization Type</InputLabel>
                  <Select
                    value={currentVisualization}
                    onChange={handleVisualizationChange}
                    label="Visualization Type"
                  >
                    {visualizationOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {visualizationOptions.find(opt => opt.value === currentVisualization)?.description}
                </Typography>
              </>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Primary Metric</InputLabel>
              <Select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                label="Primary Metric"
              >
                {metricOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {metricOptions.find(opt => opt.value === selectedMetric)?.description}
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Trust Level Filter</Typography>
            <Slider
              value={filterByTrust}
              onChange={handleTrustFilterChange}
              valueLabelDisplay="auto"
              min={1}
              max={10}
              marks={[
                { value: 1, label: '1' },
                { value: 5, label: '5' },
                { value: 10, label: '10' }
              ]}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyStrongRelationships}
                  onChange={(e) => setShowOnlyStrongRelationships(e.target.checked)}
                />
              }
              label="Strong Relationships Only (7+)"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Relationship Evolution</InputLabel>
              <Select
                value={selectedEvolution}
                onChange={(e) => setSelectedEvolution(e.target.value)}
                label="Relationship Evolution"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="strengthening">Strengthening</MenuItem>
                <MenuItem value="stable">Stable</MenuItem>
                <MenuItem value="improving">Improving</MenuItem>
                <MenuItem value="deteriorated">Deteriorated</MenuItem>
                <MenuItem value="terminated">Terminated</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={resetFilters}
              fullWidth
              sx={{ mb: 2 }}
            >
              Reset All Filters
            </Button>
          </Paper>

          {/* Stats Panel */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Network Statistics</Typography>

            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="body2" color="textSecondary">Total Relationships</Typography>
                <Typography variant="h6">{filteredRelationships.length} / {stats.total}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="body2" color="textSecondary">Avg Trust</Typography>
                <Typography variant="h6">{stats.avgTrust}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="body2" color="textSecondary">Avg Collaboration</Typography>
                <Typography variant="h6">{stats.avgCollaboration}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="body2" color="textSecondary">Avg Alignment</Typography>
                <Typography variant="h6">{stats.avgAlignment}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Evolution Trends */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Relationship Evolution</Typography>

            {Object.entries(stats.evolution).map(([evolution, count]) => (
              <Box key={evolution} sx={{ mb: 1 }}>
                <Chip
                  label={`${evolution}: ${count}`}
                  size="small"
                  color={
                    evolution === 'strengthening' ? 'success' :
                    evolution === 'deteriorated' ? 'error' :
                    evolution === 'improving' ? 'info' :
                    evolution === 'terminated' ? 'warning' :
                    'default'
                  }
                  sx={{ mr: 1 }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>
        )}

        {/* Visualization Panel */}
        <Grid
          size={{
            xs: 12,
            md: hideControls ? 12 : 8
          }}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              {useAdvancedViz ? 'Advanced Network Analysis' : visualizationOptions.find(opt => opt.value === currentVisualization)?.label}
            </Typography>

            {useAdvancedViz || currentVisualization === 'stable-force' ? (
              <AdvancedNetworkViz
                selectedMetric={selectedMetric}
                width={hideControls ? 700 : 600}
                height={600}
                filteredRelationships={filteredRelationships}
                onNodeClick={setSelectedNode}
              />
            ) : (
              <NetworkVisualizationOptions
                selectedVisualization={currentVisualization}
                width={hideControls ? 800 : 600}
                height={500}
                filteredRelationships={filteredRelationships}
                onNodeClick={setSelectedNode}
              />
            )}
          </Paper>
        </Grid>

        {/* Insights Panel */}
        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Relationship Insights</Typography>

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>High Trust Relationships</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Relationships with trust scores 8+ are characterized by vulnerability, mutual support during crisis, and consistent follow-through
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      {filteredRelationships
                        .filter(rel => (rel.trust || 0) >= 8)
                        .slice(0, 3)
                        .map((rel, idx) => (
                          <Chip
                            key={idx}
                            label={`${rel.entity1} - ${rel.entity2}`}
                            size="small"
                            color="success"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))
                      }
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Communication Excellence</Typography>
                    <Typography variant="body2" color="textSecondary">
                      High communication scores reflect regular check-ins, clear feedback, and proactive problem-solving
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Psychological Safety</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Strong psychological safety allows for honest struggles (burnout, fear, mistakes) without judgment
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Collaboration Patterns</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Best collaboration combines technical expertise with emotional intelligence and shared problem-solving
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Key Relationships */}
        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Notable Relationship Dynamics</Typography>

            <Grid container spacing={2}>
              {filteredRelationships
                .filter(rel => (rel.trust || 0) >= 8 || rel.evolution === 'strengthening' || rel.evolution === 'deteriorated')
                .slice(0, 6)
                .map((rel, index) => (
                  <Grid
                    key={index}
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {rel.entity1} ↔ {rel.entity2}
                        </Typography>
                        <Chip
                          label={rel.relationship_type || 'Unknown'}
                          size="small"
                          sx={{ mb: 1, mr: 1 }}
                        />
                        {rel.evolution && (
                          <Chip
                            label={rel.evolution}
                            size="small"
                            color={
                              rel.evolution === 'strengthening' ? 'success' :
                              rel.evolution === 'deteriorated' ? 'error' :
                              'default'
                            }
                            sx={{ mb: 1 }}
                          />
                        )}
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Trust: {rel.trust || 0}/10 | Collaboration: {rel.collaboration || 0}/10
                        </Typography>
                        {rel.notes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {rel.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              {filteredRelationships.filter(rel =>
                (rel.trust || 0) >= 8 || rel.evolution === 'strengthening' || rel.evolution === 'deteriorated'
              ).length === 0 && (
                <Grid size={12}>
                  <Typography variant="body2" color="textSecondary" align="center">
                    No notable relationships found with current filters.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {/* Node Detail Dialog */}
      <Dialog
        open={!!selectedNode}
        onClose={() => setSelectedNode(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PersonIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{selectedNode}</Typography>
              <Typography variant="body2" color="text.secondary">
                Relationship Dynamics & Network Analysis
              </Typography>
            </Box>
            <IconButton onClick={() => setSelectedNode(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedNode && (() => {
            const details = getNodeDetails(selectedNode);
            return (
              <Box>
                {/* Summary Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <GroupIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h4">{details.totalRelationships}</Typography>
                        <Typography variant="caption">Total Relationships</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <TrendingUpIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h4">{details.strongRelationships.length}</Typography>
                        <Typography variant="caption">Strong Bonds (7+)</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <TimelineIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h4">{details.evolvingRelationships.length}</Typography>
                        <Typography variant="caption">Growing Stronger</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <PsychologyIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h4">{details.concerningRelationships.length}</Typography>
                        <Typography variant="caption">Needs Attention</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                {/* Average Scores */}
                <Typography variant="h6" gutterBottom>Relationship Quality Averages</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {Object.entries(details.avgScores).map(([metric, score]) => (
                    <Grid
                      key={metric}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4
                      }}>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {metric.replace('_', ' ')}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {score.toFixed(1)}/10
                          </Typography>
                        </Box>
                        <Box sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            height: '100%',
                            width: `${(score / 10) * 100}%`,
                            bgcolor: score >= 7 ? 'success.main' : score >= 5 ? 'warning.main' : 'error.main',
                            borderRadius: 4
                          }} />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                {/* Top Relationships */}
                <Typography variant="h6" gutterBottom>Key Relationships</Typography>
                <List>
                  {details.relationships
                    .sort((a, b) => b.trust - a.trust)
                    .slice(0, 5)
                    .map((rel, index) => (
                      <ListItem key={index} divider>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="subtitle1">{rel.partner}</Typography>
                              <Chip
                                label={rel.relationship_type}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={`Trust: ${rel.trust}/10`}
                                size="small"
                                color={rel.trust >= 7 ? 'success' : rel.trust >= 5 ? 'warning' : 'error'}
                              />
                              <Chip
                                label={rel.evolution}
                                size="small"
                                color={rel.evolution === 'strengthening' ? 'success' :
                                       rel.evolution === 'deteriorated' ? 'error' : 'default'}
                              />
                            </Box>
                          }
                          secondary={rel.notes}
                        />
                      </ListItem>
                    ))}
                </List>
              </Box>
            );
          })()}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectedNode(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NetworkView;